"use client"

import { motion, AnimatePresence } from "framer-motion"
import { Card } from "@/components/ui/card"
import { ChevronDown, Search, HelpCircle, Phone, MessageCircle, CheckCircle2, Star, Shield, Clock, Truck } from "lucide-react"
import { useState, useMemo } from "react"
import { CONTACT } from "@/lib/constants"

const faqData = [
  {
    category: "Getting Started",
    icon: HelpCircle,
    color: "from-blue-500 to-cyan-500",
    items: [
      {
        question: "How long has Bigwin Packers & Movers been in business?",
        answer: "Bigwin Packers & Movers has been India's trusted relocation partner since 2008. With over 15 years of experience, we've successfully completed 50,000+ relocations across 500+ cities with a 99.8% customer satisfaction rate and industry-leading safety standards.",
        tags: ["Experience", "Trust", "History"]
      },
      {
        question: "Which cities and areas do you serve across India?",
        answer: "We provide comprehensive moving services across 500+ cities in India including all major metros (Delhi NCR, Mumbai, Bangalore, Chennai, Hyderabad, Kolkata, Pune) and tier-2/tier-3 cities. Our extensive network ensures seamless relocations whether you're moving locally, interstate, or internationally.",
        tags: ["Coverage", "Cities", "Network"]
      },
      {
        question: "Do you provide international moving and relocation services?",
        answer: "Absolutely! We're experts in international relocations with partnerships in 50+ countries. We handle complete documentation (customs clearance, permits, certificates), air/sea freight, door-to-door delivery, and destination services including temporary accommodation and local orientation.",
        tags: ["International", "Global", "Documentation"]
      },
    ],
  },
  {
    category: "Pricing & Quotes",
    icon: Star,
    color: "from-orange-500 to-red-500",
    items: [
      {
        question: "How is the moving cost calculated and what factors affect pricing?",
        answer: "Our transparent pricing is based on: distance between locations, volume/weight of items, packing services required, floor access (elevator/stairs), special handling needs, and seasonal demand. We provide detailed cost breakdowns with no hidden charges. You only pay for services you actually use.",
        tags: ["Pricing", "Transparent", "Factors"]
      },
      {
        question: "Are there any hidden charges or additional fees?",
        answer: "No hidden charges, ever! Our quotes include all standard services. Additional charges may apply only for: extra packing materials beyond estimate, storage beyond agreed period, route diversions, or additional services requested on moving day. All potential extras are clearly mentioned upfront in your quote.",
        tags: ["No Hidden Fees", "Transparent", "Honest"]
      },
      {
        question: "Do you offer discounts for bulk, corporate, or group relocations?",
        answer: "Yes, we offer attractive discounts for bulk relocations, corporate moves, and group bookings. Corporate clients get special rates, flexible payment terms, and dedicated account management. Contact us for customized enterprise quotes and volume discounts.",
        tags: ["Discounts", "Corporate", "Bulk"]
      },
    ],
  },
  {
    category: "Packing & Safety",
    icon: Shield,
    color: "from-green-500 to-emerald-500",
    items: [
      {
        question: "What premium packing materials and techniques do you use?",
        answer: "We use only premium, eco-friendly packing materials including high-grade corrugated boxes, bubble wrap, foam padding, kraft paper, and protective covers. For fragile items, we use specialized techniques like custom wooden crates, climate-controlled packaging, and shock-absorbing materials.",
        tags: ["Premium Materials", "Eco-friendly", "Quality"]
      },
      {
        question: "How do you ensure safety of fragile and valuable items?",
        answer: "Fragile and valuable items receive special attention with individual packing, photographic documentation, GPS tracking, and dedicated handling by trained specialists. We use custom wooden crates, foam padding, and climate-controlled vehicles. High-value items get separate insurance coverage.",
        tags: ["Fragile Items", "Safety", "Special Care"]
      },
      {
        question: "What insurance coverage is available for my belongings?",
        answer: "Comprehensive insurance coverage is included with every move. Our standard coverage protects up to ₹1 lakh per consignment. We also offer enhanced insurance options up to ₹10 lakhs for high-value items. Claims are processed quickly within 7-10 working days with minimal documentation.",
        tags: ["Insurance", "Coverage", "Protection"]
      },
    ],
  },
  {
    category: "Storage & Timing",
    icon: Clock,
    color: "from-purple-500 to-violet-500",
    items: [
      {
        question: "Do you provide secure storage and warehousing facilities?",
        answer: "Yes, we operate climate-controlled, secure storage facilities with 24/7 CCTV surveillance, fire safety systems, and pest control. Storage is available for short-term (days/weeks) and long-term (months/years) needs with flexible pricing and easy access arrangements.",
        tags: ["Storage", "Secure", "Climate-controlled"]
      },
      {
        question: "How long does a typical relocation take to complete?",
        answer: "Local moves within the city typically take 1-2 days, interstate moves take 3-7 days, and international relocations take 15-30 days depending on destination and customs clearance. We provide real-time tracking and regular updates throughout your move.",
        tags: ["Timeline", "Duration", "Tracking"]
      },
      {
        question: "Can you accommodate weekend, holiday, or emergency moves?",
        answer: "Absolutely! We offer flexible scheduling including weekends, holidays, and emergency relocations. Weekend and holiday moves are available at standard rates with no additional charges. For emergency moves, we can mobilize within 24-48 hours subject to availability.",
        tags: ["Flexible", "Weekend", "Emergency"]
      },
    ],
  },
]

export function FAQSection() {
  const [expandedCategory, setExpandedCategory] = useState(0)
  const [expandedFAQ, setExpandedFAQ] = useState<string | null>("0-0")
  const [searchTerm, setSearchTerm] = useState("")

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
    },
  }

  const categoryVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.5 }
    },
  }

  const filteredFaq = useMemo(() => {
    if (!searchTerm.trim()) return faqData

    const lowerSearch = searchTerm.toLowerCase()
    return faqData
      .map((category) => ({
        ...category,
        items: category.items.filter(
          (item) =>
            item.question.toLowerCase().includes(lowerSearch) || 
            item.answer.toLowerCase().includes(lowerSearch) ||
            item.tags.some(tag => tag.toLowerCase().includes(lowerSearch)),
        ),
      }))
      .filter((category) => category.items.length > 0)
  }, [searchTerm])

  return (
    <section 
      className="relative py-24 sm:py-32 overflow-hidden"
      aria-labelledby="faq-section-heading"
    >
      {/* Modern gradient background matching brand colors */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-blue-50/30" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_25%,rgba(37,99,235,0.05),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_75%,rgba(249,115,22,0.05),transparent_50%)]" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Enhanced Header Section */}
        <motion.header 
          className="text-center mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={itemVariants}
        >
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-100 to-orange-100 text-blue-700 font-medium text-sm mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <HelpCircle size={16} />
            Get Instant Answers
          </motion.div>
          
          <h1 
            id="faq-section-heading"
            className="text-4xl sm:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-gray-900 via-blue-900 to-orange-900 bg-clip-text text-transparent mb-6"
          >
            Frequently Asked Questions
          </h1>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-8">
            Everything you need to know about Bigwin Packers & Movers. 
            Find answers to common questions or contact our 24/7 support team.
          </p>

          {/* Enhanced Search */}
          <motion.div
            className="max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search FAQs, topics, or keywords..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none text-base bg-white/80 backdrop-blur-sm shadow-lg"
              />
            </div>
          </motion.div>
        </motion.header>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Category Navigation */}
          <motion.nav 
            className="lg:col-span-1"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
          >
            <div className="sticky top-8 space-y-3">
              {faqData.map((category, index) => {
                const Icon = category.icon
                // Show all categories, but dim the ones that don't have search results
                const hasResults = searchTerm ? filteredFaq.some(cat => cat.category === category.category) : true
                const isActive = !searchTerm && expandedCategory === index
                
                return (
                  <motion.button
                    key={category.category}
                    variants={categoryVariants}
                    onClick={() => {
                      if (searchTerm) {
                        // Clear search when clicking a category
                        setSearchTerm("")
                      }
                      setExpandedCategory(index)
                      setExpandedFAQ(`${index}-0`)
                    }}
                    disabled={Boolean(searchTerm && !hasResults)}
                    className={`w-full p-4 rounded-xl text-left transition-all duration-300 ${
                      isActive
                        ? 'bg-white shadow-lg border-2 border-blue-200'
                        : searchTerm && !hasResults
                        ? 'bg-gray-100/60 opacity-50 cursor-not-allowed border-2 border-transparent'
                        : 'bg-white/60 hover:bg-white/80 border-2 border-transparent'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${category.color} flex items-center justify-center ${
                        searchTerm && !hasResults ? 'opacity-50' : ''
                      }`}>
                        <Icon size={20} className="text-white" />
                      </div>
                      <div>
                        <h3 className={`font-semibold ${
                          searchTerm && !hasResults ? 'text-gray-400' : 'text-gray-900'
                        }`}>
                          {category.category}
                        </h3>
                        <p className={`text-sm ${
                          searchTerm && !hasResults ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          {searchTerm && hasResults 
                            ? `${filteredFaq.find(cat => cat.category === category.category)?.items.length || 0} results`
                            : `${category.items.length} questions`
                          }
                        </p>
                      </div>
                    </div>
                  </motion.button>
                )
              })}
            </div>
          </motion.nav>

          {/* FAQ Content */}
          <motion.div 
            className="lg:col-span-3"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
          >
            {searchTerm ? (
              // Search Results View
              <div className="space-y-6">
                {filteredFaq.length === 0 ? (
                  <motion.div 
                    className="text-center py-12"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                      <Search size={24} className="text-gray-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No results found</h3>
                    <p className="text-gray-600">Try different keywords or browse categories above</p>
                  </motion.div>
                ) : (
                  filteredFaq.map((category, categoryIdx) => (
                    <div key={category.category} className="space-y-4">
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${category.color} flex items-center justify-center`}>
                          <category.icon size={16} className="text-white" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900">{category.category}</h2>
                      </div>
                      
                      {category.items.map((faq, faqIndex) => {
                        const faqId = `search-${categoryIdx}-${faqIndex}`
                        const isExpanded = expandedFAQ === faqId
                        
                        return (
                          <motion.article
                            key={faqId}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: faqIndex * 0.1 }}
                            className="bg-white/90 backdrop-blur-sm rounded-2xl border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden"
                          >
                            <button
                              onClick={() => setExpandedFAQ(isExpanded ? null : faqId)}
                              className="w-full p-6 text-left hover:bg-gray-50/50 transition-colors"
                              aria-expanded={isExpanded}
                            >
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                  <h3 className="font-semibold text-gray-900 text-lg mb-2 pr-4">
                                    {faq.question}
                                  </h3>
                                  <div className="flex flex-wrap gap-2">
                                    {faq.tags.map((tag, tagIndex) => (
                                      <span
                                        key={tagIndex}
                                        className="px-2 py-1 rounded-md bg-blue-100 text-blue-700 text-xs font-medium"
                                      >
                                        {tag}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                                <motion.div
                                  animate={{ rotate: isExpanded ? 180 : 0 }}
                                  transition={{ duration: 0.3 }}
                                  className="flex-shrink-0 mt-1"
                                >
                                  <ChevronDown size={24} className="text-blue-600" />
                                </motion.div>
                              </div>
                            </button>

                            <AnimatePresence>
                              {isExpanded && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: "auto" }}
                                  exit={{ opacity: 0, height: 0 }}
                                  transition={{ duration: 0.3, ease: "easeInOut" }}
                                  className="border-t border-gray-100"
                                >
                                  <div className="p-6 bg-gradient-to-br from-blue-50/30 to-orange-50/30">
                                    <p className="text-gray-700 leading-relaxed text-base">
                                      {faq.answer}
                                    </p>
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </motion.article>
                        )
                      })}
                    </div>
                  ))
                )}
              </div>
            ) : (
              // Category View
              <div className="space-y-4">
                {faqData[expandedCategory].items.map((faq, faqIndex) => {
                  const faqId = `${expandedCategory}-${faqIndex}`
                  const isExpanded = expandedFAQ === faqId
                  
                  return (
                    <motion.article
                      key={faqId}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: faqIndex * 0.1 }}
                      className="bg-white/90 backdrop-blur-sm rounded-2xl border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden"
                    >
                      <button
                        onClick={() => setExpandedFAQ(isExpanded ? null : faqId)}
                        className="w-full p-6 text-left hover:bg-gray-50/50 transition-colors"
                        aria-expanded={isExpanded}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 text-lg mb-2 pr-4">
                              {faq.question}
                            </h3>
                            <div className="flex flex-wrap gap-2">
                              {faq.tags.map((tag, tagIndex) => (
                                <span
                                  key={tagIndex}
                                  className="px-2 py-1 rounded-md bg-blue-100 text-blue-700 text-xs font-medium"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                          <motion.div
                            animate={{ rotate: isExpanded ? 180 : 0 }}
                            transition={{ duration: 0.3 }}
                            className="flex-shrink-0 mt-1"
                          >
                            <ChevronDown size={24} className="text-blue-600" />
                          </motion.div>
                        </div>
                      </button>

                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                            className="border-t border-gray-100"
                          >
                            <div className="p-6 bg-gradient-to-br from-blue-50/30 to-orange-50/30">
                              <p className="text-gray-700 leading-relaxed text-base">
                                {faq.answer}
                              </p>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.article>
                  )
                })}
              </div>
            )}
          </motion.div>
        </div>

        {/* Enhanced Contact Section */}
        <motion.div
          className="mt-20 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="max-w-4xl mx-auto p-8 rounded-2xl bg-gradient-to-br from-blue-50 to-orange-50 border border-blue-100">
            <div className="flex items-center justify-center gap-2 mb-4">
              <CheckCircle2 size={24} className="text-green-600" />
              <h3 className="text-2xl font-bold text-gray-900">Still Have Questions?</h3>
            </div>
            <p className="text-lg text-gray-600 mb-6">
              Our expert team is available 24/7 to help you with any questions about your move.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <motion.button
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-orange-600 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Phone size={20} />
                Call Now:  {CONTACT.phone.primary}
              </motion.button>
              
              <motion.button
                className="flex items-center gap-2 px-6 py-3 bg-white text-blue-600 font-semibold rounded-full border-2 border-blue-200 hover:bg-blue-50 transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <MessageCircle size={20} />
                Live Chat Support
              </motion.button>
            </div>
            
            <div className="flex items-center justify-center gap-6 mt-6 text-sm text-gray-500">
              <div className="flex items-center gap-1">
                <CheckCircle2 size={14} className="text-green-500" />
                <span>Instant Response</span>
              </div>
              <div className="flex items-center gap-1">
                <CheckCircle2 size={14} className="text-green-500" />
                <span>Expert Guidance</span>
              </div>
              <div className="flex items-center gap-1">
                <CheckCircle2 size={14} className="text-green-500" />
                <span>100% Free</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
